import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { type Capability, capabilitiesFor } from './capabilities';

export interface AuthUser {
  id: string;
  username: string;
  role: 'admin' | 'user';
  mustChangePassword: boolean;
}

interface AuthState {
  jwt: string | null;
  expiresAt: string | null;
  user: AuthUser | null;
  tenantSlug: string | null;

  setSession: (params: { jwt: string; expiresAt: string; user: AuthUser; tenantSlug: string }) => void;
  clearMustChangePassword: () => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasCapability: (cap: Capability) => boolean;
}

const STORAGE_KEY = 'nucleus.admin.auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      jwt: null,
      expiresAt: null,
      user: null,
      tenantSlug: null,

      setSession: ({ jwt, expiresAt, user, tenantSlug }) => set({ jwt, expiresAt, user, tenantSlug }),

      clearMustChangePassword: () => {
        const u = get().user;
        if (!u) return;
        set({ user: { ...u, mustChangePassword: false } });
      },

      logout: () => set({ jwt: null, expiresAt: null, user: null, tenantSlug: null }),

      isAuthenticated: () => {
        const { jwt, expiresAt } = get();
        if (!jwt || !expiresAt) return false;
        return new Date(expiresAt).getTime() > Date.now();
      },

      hasCapability: (cap) => capabilitiesFor(get().user?.role).includes(cap),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ jwt: s.jwt, expiresAt: s.expiresAt, user: s.user, tenantSlug: s.tenantSlug }),
    },
  ),
);
